import SearchableFilter from "@/components/ui/SearchableFilter";
import { Button } from "@/components/ui/button";
import { convertMonthsToYears } from "@component/formfields/EditFormTailwind";
import { X } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { ReactComponent as FilterAltIcon } from "../../../../../../assests/filter.svg";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";


function useRadixPopperHeight(open) {
  const [popperHeight, setPopperHeight] = useState(400);

  useEffect(() => {
    if (!open) return;

    const update = () => {
      const wrapper = document.querySelector('[data-radix-popper-content-wrapper]');
      if (!wrapper) return;
      const val = getComputedStyle(wrapper).getPropertyValue('--radix-popper-available-height');
      const parsed = parseInt(val, 10);
      if (!isNaN(parsed)) setPopperHeight(parsed);
    };

    // small delay gives Radix time to mount the popper in the DOM
    const timer = setTimeout(update, 10);
    window.addEventListener('resize', update);
    return () => {
      clearTimeout(timer);
      window.removeEventListener('resize', update);
    };
  }, [open]);

  return popperHeight;
}


const FilterPopover = ({ column, containerRef }) => {
  const [open, setOpen] = useState(false);
  const [filterValue, setFilterValueState] = useState(
    column.getFilterValue?.() ?? []
  );

  // ✅ Map column values to readable labels
  const filterValues = useMemo(() => {
    if (!column?.getFacetedUniqueValues) return [];
    const uniqueValues = Array.from(column.getFacetedUniqueValues().keys())
      .map(val => (typeof val === "string" ? val.trim() : val))
      .filter(val => val !== null && val !== undefined && val !== "");

    if (column.id === "status_") {
      const mapped = new Set();
      uniqueValues.forEach((val) => {
      const trimmedVal = typeof val === "string" ? val.trim() : val;
      if (trimmedVal === "TRUE") {
        mapped.add("Active");
      } else if (trimmedVal === "FALSE") {
        mapped.add("In-Active");
      }
      });
      return Array.from(mapped);
    }
    // Special case for allocation_status column
    if (column.id === "allocation_status") {
      const mapped = new Set();
      uniqueValues.forEach((val) => {
        if (val === "Y") {
          mapped.add("Allocated");
        } else if (val === "N" || val === "P") {
          mapped.add("Not-Allocated");
        }
      });

      return Array.from(mapped);
    }
    // Convert to years if YOE columns, and ensure uniqueness
    let mappedValues = uniqueValues.map((val) => {
      // if (["total_yoe", "wavicle_yoe", "yoe_before_wavicle"].includes(column.id)) {
      //   return convertMonthsToYears(val).toString();
      // }
      return typeof val === "number" ? val.toString() : val;
    });

    return Array.from(new Set(mappedValues));
  }, [column]);

  const handleApplyClick = () => {
    if (column.id === "allocation_status") {
      // Map UI values ("allocated", "not-allocated") back to raw column values ("Y", "N", "P")
      const mappedValues = filterValue.flatMap((val) => {
        if (val === "Allocated") return ["Y"];
        if (val === "Not-Allocated") return ["N", "P"];
        return [];
      });
      column.setFilterValue?.(
        mappedValues.length > 0 ? mappedValues : undefined
      );
    } else if (column.id === "status_") {
      // Map UI values ("Active", "In-Active") back to raw column values ("TRUE", "FALSE")
      const mappedValues = filterValue.flatMap((val) => {
        if (val === "Active") return ["TRUE"];
        if (val === "In-Active") return ["FALSE"];
        return [];
      });
      column.setFilterValue?.(
        mappedValues.length > 0 ? mappedValues : undefined
      );
    } 
    else {
      let normalizedValues;
      // if (["total_yoe", "wavicle_yoe", "yoe_before_wavicle"].includes(column.id)) {
      //   // Convert back years → months for filtering
      //   normalizedValues = filterValue.map((val) => Math.round(parseFloat(val) * 12));
      // } 
      // else {
        normalizedValues = filterValue.map((val) =>
          !isNaN(val) && val.trim() !== "" ? parseFloat(val) : val
        );
      // }
      column.setFilterValue?.(
        normalizedValues.length > 0 ? normalizedValues : undefined
      );
    }

    setOpen(false);
  };

  const handleClearClick = () => {
    setFilterValueState([]);
    column.setFilterValue?.(undefined);
  };

  const isFiltered = column?.getIsFiltered?.();

  const popperHeight = useRadixPopperHeight(open) || 400;


  return (
    <>
<Popover  open={open} onOpenChange={setOpen}>
  <PopoverTrigger asChild>
    <Button variant="outline" className="p-1 h-auto bg-transparent shadow-none border-none">
      <FilterAltIcon />
    </Button>

  </PopoverTrigger>
        {isFiltered && (
        <Button variant="outline" className="p-1 h-auto bg-transparent shadow-none border-none" 
          size="small"
          onClick={handleClearClick}
          title="Clear Filter"
        >
          <X fontSize="small" />
        </Button>
      )}
  <PopoverContent disabledPortal={false} containerRef={{current:containerRef}} className="p-0 w-64">

        <div className="p-1 w-full max-h-[--radix-popper-available-height]">
          <SearchableFilter
            id={`${column.id}-filter`}
            multiple
            value={filterValue}
            data={filterValues}
            height={popperHeight-110}
            onChange={(value) => {
              setFilterValueState(value);
            }}
          />
          <div className="flex gap-2 items-center justify-end mt-2">
            <Button
            variant="outline"
              className="!bg-primary-foreground !text-form-label"
              disabled={filterValue.length === 0}
              onClick={handleClearClick}
              size="sm"
            >
              Clear
            </Button>
            <Button
              disabled={filterValue.length === 0}
              onClick={handleApplyClick}
              size='sm'
            >
              Apply
            </Button>
          </div>
        </div>
        </PopoverContent>

</Popover>
    </>
  );
};

export default FilterPopover;
